<template>
  <v-dialog
    :value="value"
    max-width="560"
    overlay-color="black"
    overlay-opacity="0.75"
    content-class="welcome-dialog-content"
    @input="$emit('input', $event)"
  >
    <v-card dark class="welcome-card">
      <div class="welcome-hero">
        <div class="welcome-badge">
          <v-icon size="30" color="#4395a3">mdi-human-greeting</v-icon>
        </div>
        <h2 class="welcome-title">
          {{ greeting }}
        </h2>
        <p class="welcome-subtitle">
          Your workspace is ready. Here's what you can do right away:
        </p>
      </div>

      <v-card-text class="welcome-body">
        <ul class="welcome-features">
          <li v-for="feature in features" :key="feature.title">
            <span class="welcome-feature-icon">
              <v-icon size="20" color="#4395a3">{{ feature.icon }}</v-icon>
            </span>
            <span class="welcome-feature-copy">
              <strong>{{ feature.title }}</strong>
              <span class="welcome-feature-text">{{ feature.text }}</span>
            </span>
          </li>
        </ul>

        <div v-if="showPlans" class="welcome-upsell">
          <div class="welcome-upsell-copy">
            <p class="welcome-upsell-title">
              <v-icon size="16" color="#4395a3" class="mr-1">
                mdi-creation
              </v-icon>
              Unlock AI-powered workflows
            </p>
            <p class="welcome-upsell-text">
              AI segmentation, study triage and PACS integration are available
              on paid plans — upgrade whenever you're ready.
            </p>
          </div>
          <button class="welcome-upsell-btn" @click="explorePlans">
            Explore plans
          </button>
        </div>
      </v-card-text>

      <v-card-actions class="welcome-actions">
        <v-btn
          block
          large
          depressed
          color="primary"
          class="welcome-cta"
          @click="close"
        >
          Start exploring
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * One-time welcome modal for newly registered users.
 * Visibility rules live in App.vue (recent signup + not seen before);
 * this component only renders the content and emits intents.
 */
export default {
  name: "WelcomeDialog",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    userName: {
      type: String,
      default: ""
    },
    showPlans: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    greeting() {
      const name = (this.userName || "").trim();
      return name ? `Welcome to DICOM Vision, ${name}!` : "Welcome to DICOM Vision!";
    },
    features() {
      return [
        {
          icon: "mdi-cloud-upload-outline",
          title: "Upload your first study",
          text:
            "Drag & drop DICOM files or import from disk — they're viewable in seconds."
        },
        {
          icon: "mdi-cube-scan",
          title: "Explore advanced visualization",
          text:
            "MPR, 3D volume rendering and precise measurements, right in your browser."
        },
        {
          icon: "mdi-account-group-outline",
          title: "Share & collaborate",
          text:
            "Create secure share links and discuss studies with colleagues in real time."
        }
      ];
    }
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    explorePlans() {
      this.$emit("input", false);
      this.$emit("explore-plans");
    }
  }
};
</script>

<style scoped>
.welcome-card {
  background: linear-gradient(165deg, #1e1e1e, #171717 58%) !important;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 20px !important;
  overflow: hidden;
}

.welcome-hero {
  padding: 32px 32px 0;
  text-align: center;
  background: radial-gradient(
    ellipse at top,
    rgba(67, 149, 163, 0.14),
    transparent 65%
  );
}

.welcome-badge {
  align-items: center;
  background: rgba(67, 149, 163, 0.14);
  border: 1px solid rgba(67, 149, 163, 0.4);
  border-radius: 50%;
  display: inline-flex;
  height: 62px;
  justify-content: center;
  margin-bottom: 16px;
  width: 62px;
}

.welcome-title {
  color: #f5f7fb;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 8px;
}

.welcome-subtitle {
  color: #b5bcc7;
  font-size: 14px;
  margin-bottom: 0;
}

.welcome-body {
  padding: 20px 32px 4px !important;
}

.welcome-features {
  list-style: none;
  margin: 0 0 18px;
  padding: 0;
}

.welcome-features li {
  align-items: flex-start;
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
}

.welcome-feature-icon {
  align-items: center;
  background: rgba(67, 149, 163, 0.1);
  border-radius: 10px;
  display: inline-flex;
  flex-shrink: 0;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.welcome-feature-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.welcome-feature-copy strong {
  color: #f0f2f6;
  font-size: 14px;
}

.welcome-feature-text {
  color: #a9b0bc;
  font-size: 13px;
  line-height: 1.4;
}

.welcome-upsell {
  align-items: center;
  background: rgba(67, 149, 163, 0.08);
  border: 1px solid rgba(67, 149, 163, 0.3);
  border-radius: 14px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 14px 16px;
}

.welcome-upsell-title {
  align-items: center;
  color: #e7edf5;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 4px;
}

.welcome-upsell-text {
  color: #a9b0bc;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 0;
}

.welcome-upsell-btn {
  background: rgba(67, 149, 163, 0.16);
  border: 0;
  border-radius: 10px;
  color: #4fb3c4;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 16px;
  white-space: nowrap;
}

.welcome-upsell-btn:hover {
  background: rgba(67, 149, 163, 0.28);
}

.welcome-actions {
  padding: 16px 32px 26px !important;
}

.welcome-cta {
  border-radius: 12px !important;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
}

@media (max-width: 560px) {
  .welcome-hero,
  .welcome-body,
  .welcome-actions {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .welcome-upsell {
    align-items: stretch;
    flex-direction: column;
  }

  .welcome-upsell-btn {
    width: 100%;
  }
}
</style>

<style>
.welcome-dialog-content {
  border-radius: 20px;
}
</style>
